/**
 * The ride sheet: one order on paper — who, when, where, the map, what happened and what it
 * cost — for a complaint, an insurer or the police.
 *
 * Rendered into the document's body rather than inside the page, and hidden everywhere but
 * the printer: the console's shell is a fixed-height app with its own scrolling panes, and a
 * stylesheet that tried to unpick that into a paper flow would be a second layout to keep
 * working. A sheet of its own is one page, laid out for paper from the start.
 */

import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { useTenantFormats } from '../shell/formats';
import { distanceWords, fareLineWords, fareLinesToShow, type JourneyDetail } from './journey';

import './JourneySheet.css';

export function JourneySheet({
  journey,
  snapshot,
}: {
  journey: JourneyDetail;
  snapshot: string | null;
}) {
  const { t } = useTranslation();
  const formats = useTenantFormats();
  const m = journey.measures;

  const facts: [string, string | null | undefined][] = [
    [t('reports.journeys.sheet.order'), journey.reference],
    [
      t('reports.journeys.sheet.state'),
      t(`states.${journey.state}`, { defaultValue: journey.state }),
    ],
    [t('reports.journeys.sheet.requested'), formats.moment(journey.requestedAt)],
    [
      t('reports.journeys.sheet.client'),
      [journey.customerName, journey.customerPhone].filter(Boolean).join(' · ') || null,
    ],
    [t('reports.journeys.sheet.from'), journey.pickupAddress],
    [
      t('reports.journeys.sheet.to'),
      journey.stops.length > 0 ? journey.stops[journey.stops.length - 1].address : null,
    ],
    [
      t('reports.journeys.sheet.driver'),
      journey.legs
        .map((leg) =>
          [leg.driverName, leg.callSign, leg.plate, leg.vehicle].filter(Boolean).join(' · '),
        )
        .join(' → ') || null,
    ],
    [
      t('reports.journeys.measures.accepted'),
      m.acceptedAt ? formats.timeWithSeconds(m.acceptedAt) : null,
    ],
    [
      t('reports.journeys.measures.expected'),
      m.expectedAt ? formats.timeWithSeconds(m.expectedAt) : null,
    ],
    [
      t('reports.journeys.measures.arrived'),
      m.arrivedAt
        ? [
            formats.timeWithSeconds(m.arrivedAt),
            m.lateSeconds == null
              ? null
              : m.lateSeconds >= 0
                ? t('reports.journeys.measures.late', { time: formats.duration(m.lateSeconds) })
                : t('reports.journeys.measures.early', { time: formats.duration(-m.lateSeconds) }),
          ]
            .filter(Boolean)
            .join(' · ')
        : null,
    ],
    [
      t('reports.journeys.measures.waited'),
      m.waitSeconds != null ? formats.duration(m.waitSeconds) : null,
    ],
    [
      t('reports.journeys.measures.ride'),
      m.rideDrivenMetres != null ? distanceWords(t, formats, m.rideDrivenMetres) : null,
    ],
    [
      t('reports.journeys.sheet.recommended'),
      m.rideRecommendedMetres != null ? distanceWords(t, formats, m.rideRecommendedMetres) : null,
    ],
    [
      t('reports.journeys.measures.ended'),
      m.endedFromDestinationMetres != null
        ? t('reports.journeys.measures.fromDestination', {
            distance: distanceWords(t, formats, m.endedFromDestinationMetres),
          })
        : null,
    ],
    [
      t('reports.journeys.measures.fare'),
      (m.meteredFare ?? m.finalFare) != null ? formats.money(m.meteredFare ?? m.finalFare) : null,
    ],
  ];

  return createPortal(
    <section className="journey-sheet" aria-hidden="true">
      <h1>{t('reports.journeys.sheet.title', { reference: journey.reference })}</h1>

      <table className="journey-sheet-facts">
        <tbody>
          {facts
            .filter(([, value]) => value != null && value !== '')
            .map(([label, value]) => (
              <tr key={label}>
                <th>{label}</th>
                <td>{value}</td>
              </tr>
            ))}
        </tbody>
      </table>

      {snapshot !== null && <img className="journey-sheet-map" src={snapshot} alt="" />}

      {journey.problems.length > 0 && (
        <p>
          <strong>{t('reports.journeys.sheet.problems')}</strong>{' '}
          {journey.problems.map((p) => t(`reports.journeys.problems.${p}`)).join(', ')}
        </p>
      )}

      <h2>{t('reports.journeys.events.title')}</h2>
      <table className="journey-sheet-events">
        <tbody>
          {journey.events.map((event, index) => (
            <tr key={`${event.kind}-${event.at}-${index}`}>
              <td>{formats.timeWithSeconds(event.at)}</td>
              <td>
                {t(`reports.journeys.events.${event.kind}`, {
                  driver: event.driverName ?? t('reports.journeys.someDriver'),
                  who:
                    event.actorName ?? t(`actors.${event.actor ?? 'System'}`, { defaultValue: '' }),
                  stars: event.stars ?? 0,
                  address: event.text ?? '',
                })}
                {event.reason &&
                ![
                  'stopReached',
                  'destinationReached',
                  'offered',
                  'created',
                  'streetRide',
                  'setOff',
                  'arrived',
                  'pickedUp',
                  'completed',
                ].includes(event.kind)
                  ? ` · ${event.reason}`
                  : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {fareLinesToShow(m.fareLines).length > 0 && (
        <>
          <h2>{t('reports.journeys.measures.fareLines')}</h2>
          <table className="journey-sheet-events">
            <tbody>
              {fareLinesToShow(m.fareLines).map((line) => (
                <tr key={line.code}>
                  <td>{fareLineWords(t, line, formats.decimal)}</td>
                  <td>{formats.money(line.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <p className="journey-sheet-foot">
        {t('reports.journeys.sheet.printed', { when: formats.moment(new Date().toISOString()) })}
      </p>
    </section>,
    document.body,
  );
}
